import { create } from 'zustand';
import { api } from '@/api/apiInstance';
import { apiConfig } from '@/config';

const DS_TOKEN_KEY = 'DSToken';

/** A successful server validation is trusted for this long before re-checking. */
const VALIDATION_TTL_MS = 5 * 60 * 1000;

// Shared across every caller: many components mount useDocuSignAuth at once
// (e.g. one per document row), and each used to fire its own GetUserInfo call.
let inflightValidation: Promise<boolean> | null = null;
let lastValidatedAt = 0;

interface DocuSignToken {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  expiresAt?: number;
}

interface DocuSignState {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isValidating: boolean;
  setIsAuthenticated: (value: boolean) => void;
  setIsAuthenticating: (value: boolean) => void;
  checkTokenOnInit: () => void;
  validateTokenWithServer: () => Promise<boolean>;
}

export const useDocuSignStore = create<DocuSignState>()((set, get) => ({
  isAuthenticated: false,
  isAuthenticating: false,
  isValidating: false,

  setIsAuthenticated: (value) => set({ isAuthenticated: value }),
  setIsAuthenticating: (value) => set({ isAuthenticating: value }),

  /**
   * Validate the stored DS token against the server by calling /DSConnect/GetUserInfo.
   * This matches Angular's edit-job-step.component.ts approach which calls the same
   * endpoint to verify the token is truly valid with DocuSign, not just present in localStorage.
   *
   * Uses api.instance (Axios) so ABP auth headers (Bearer token, __tenant) are
   * automatically included by the request interceptor.
   */
  validateTokenWithServer: async (): Promise<boolean> => {
    // Collapse concurrent callers into one request, and trust a recent
    // successful validation instead of re-hitting the server per component.
    if (inflightValidation) {
      return inflightValidation;
    }
    if (get().isAuthenticated && Date.now() - lastValidatedAt < VALIDATION_TTL_MS) {
      return true;
    }

    const storedToken = localStorage.getItem(DS_TOKEN_KEY);
    if (!storedToken) {
      set({ isAuthenticated: false, isValidating: false });
      return false;
    }

    let token: DocuSignToken;
    try {
      token = JSON.parse(storedToken);
      if (!token.access_token) {
        set({ isAuthenticated: false, isValidating: false });
        localStorage.removeItem(DS_TOKEN_KEY);
        return false;
      }
    } catch {
      set({ isAuthenticated: false, isValidating: false });
      localStorage.removeItem(DS_TOKEN_KEY);
      return false;
    }

    // Quick client-side expiration check before making server call
    if (token.expiresAt && token.expiresAt <= Date.now()) {
      set({ isAuthenticated: false, isValidating: false });
      localStorage.removeItem(DS_TOKEN_KEY);
      return false;
    }

    set({ isValidating: true });

    inflightValidation = (async () => {
      try {
        const response = await api.instance.get(
          `${apiConfig.baseUrl}/DSConnect/GetUserInfo`,
          {
            headers: {
              ds_access_token: token.access_token,
            },
          }
        );

        const result = response.data;
        // Match Angular: if result is not null and has no error, token is valid
        if (result !== null && !result.error) {
          lastValidatedAt = Date.now();
          set({ isAuthenticated: true, isValidating: false });
          return true;
        }

        // Server says token is invalid - clear it
        set({ isAuthenticated: false, isValidating: false });
        localStorage.removeItem(DS_TOKEN_KEY);
        return false;
      } catch {
        // Network error or 401/403 - don't clear token, but mark as not authenticated
        // so user can retry when connectivity is restored
        set({ isAuthenticated: false, isValidating: false });
        return false;
      } finally {
        inflightValidation = null;
      }
    })();

    return inflightValidation;
  },

  checkTokenOnInit: () => {
    try {
      const storedToken = localStorage.getItem(DS_TOKEN_KEY);
      if (storedToken) {
        const token: DocuSignToken = JSON.parse(storedToken);
        const now = Date.now();

        // Check if token has expiration info and is expired
        if (token.expiresAt && token.expiresAt <= now) {
          set({ isAuthenticated: false });
          localStorage.removeItem(DS_TOKEN_KEY);
          return;
        }

        // Token exists and isn't expired client-side - validate with server
        if (token.access_token) {
          // Trigger async server validation; keep isAuthenticated false until confirmed
          get().validateTokenWithServer();
          return;
        }
      }
      set({ isAuthenticated: false });
    } catch {
      set({ isAuthenticated: false });
      localStorage.removeItem(DS_TOKEN_KEY);
    }
  },
}));

// Initialize on module load - check for existing token
if (typeof window !== 'undefined') {
  useDocuSignStore.getState().checkTokenOnInit();
}
